// Minimal color utilities for the pipeline.
// We work in 8-bit sRGB. Distance is straight Euclidean — adequate for the
// 4–32 colour palettes we quantize against. OKLCH/Lab can land later if a
// specific palette demands it.

export type RGB = readonly [number, number, number];

export function parseHex(input: string): RGB | null {
  const s = input.trim().replace(/^#/, '');
  if (s.length === 3) {
    const r = parseInt(s[0]! + s[0]!, 16);
    const g = parseInt(s[1]! + s[1]!, 16);
    const b = parseInt(s[2]! + s[2]!, 16);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
    return [r, g, b];
  }
  if (s.length === 6) {
    const r = parseInt(s.slice(0, 2), 16);
    const g = parseInt(s.slice(2, 4), 16);
    const b = parseInt(s.slice(4, 6), 16);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
    return [r, g, b];
  }
  return null;
}

export function toHex(rgb: RGB): string {
  return (
    '#' +
    rgb
      .map((c) => clamp255(c).toString(16).padStart(2, '0'))
      .join('')
  );
}

export function distSq(a: RGB, b: RGB): number {
  const dr = a[0] - b[0];
  const dg = a[1] - b[1];
  const db = a[2] - b[2];
  return dr * dr + dg * dg + db * db;
}

/** Returns the index in `palette` whose RGB is closest (Euclidean) to `rgb`. */
export function nearestIndex(rgb: RGB, palette: readonly RGB[]): number {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < palette.length; i++) {
    const d = distSq(rgb, palette[i] as RGB);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

export function lerpRGB(a: RGB, b: RGB, t: number): RGB {
  return [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
  ];
}

export function luminance(rgb: RGB): number {
  // Rec. 709 coefficients on raw sRGB (good enough for sort/contrast UI).
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}

function clamp255(n: number): number {
  return n < 0 ? 0 : n > 255 ? 255 : Math.round(n);
}
